import React from 'react';
import { useTournament } from '../hooks/useTournament';

const PRESET_COLORS = ['#4CAF50', '#F44336', '#2196F3', '#FF9800', '#FFEB3B', '#E91E63', '#FFFFFF', '#9E9E9E'];

const ColorSwatch = ({ color, onClick, selected }) => (
  <button
    type="button"
    className={`color-swatch${selected ? ' selected' : ''}`}
    onClick={() => onClick(color)}
    aria-label={`Wähle Farbe ${color}`}
    style={{ backgroundColor: color }}
  />
);

const SettingsSummary = () => {
  const { players, numFields, numTeams, numRounds, teamColors = [], updateTeamColor } = useTournament();
  const playerCount = players.length;
  const minPerTeam = numTeams > 0 ? Math.floor(playerCount / numTeams) : 0;
  const extra = numTeams > 0 ? playerCount % numTeams : 0;

  return (
    <div className="section">
      <div className="section-header">
        <h2>Aktuelle Einstellungen</h2>
      </div>
      <table className="tournament-table settings-summary-table">
        <thead>
          <tr>
            <th>Einstellung</th>
            <th>Wert</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Spieler</td>
            <td>{playerCount}</td>
          </tr>
          <tr>
            <td>Felder</td>
            <td>{numFields}</td>
          </tr>
          <tr>
            <td>Teams</td>
            <td>{numTeams}</td>
          </tr>
          <tr>
            <td>Runden</td>
            <td>{numRounds}</td>
          </tr>
          <tr>
            <th colSpan="2">Team-Zuweisungen <span className="small-note">(Leibchenfarben)</span></th>
          </tr>
          <tr>
            <td colSpan="2" style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>
              <small style={{ color: 'var(--text-color)', opacity: 0.8 }}>Leibchenfarben: Wähle eine voreingestellte Farbe, verwende "M" für die manuelle Auswahl oder Ø für transparent (keine Farbe). Diese Farben werden in der Matrix und beim Drucken verwendet.</small>
            </td>
          </tr>
          {Array.from({ length: numTeams }).map((_, idx) => {
            const count = idx < extra ? minPerTeam + 1 : minPerTeam;
            const label = String.fromCharCode(65 + idx);
            // storedColor can be null (explicit transparent) or a hex string
            const storedColor = teamColors[idx];
            const displayColor = (storedColor === null || storedColor === '') ? 'transparent' : (storedColor || PRESET_COLORS[idx % PRESET_COLORS.length]);
            // color input needs a valid hex value when opened; fall back to a sensible preset
            const colorInputValue = (typeof storedColor === 'string' && storedColor.startsWith('#')) ? storedColor : PRESET_COLORS[idx % PRESET_COLORS.length];
            return (
              <tr key={idx} className="team-row">
                <td style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="team-pill" style={{ backgroundColor: displayColor, width: 28, height: 18, borderRadius: 4, border: '1px solid rgba(0,0,0,0.2)' }} />
                    <span>Team {label}</span>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: 26 }}>{count}</span>
                    {/* Manual color picker: hidden input + compact "M" button to trigger it */}
                    {/* Preset swatches */}
                    <div style={{ display: 'flex', gap: '6px', marginLeft: 8 }}>
                      {PRESET_COLORS.map((pc) => (
                        <ColorSwatch key={pc} color={pc} onClick={(c) => updateTeamColor(idx, c)} selected={storedColor && pc.toLowerCase() === (storedColor || '').toLowerCase()} />
                      ))}
                    </div>
                    {/* Wrap the input and button so the invisible input overlays the button and reliably receives clicks; placed after swatches */}
                    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: 22, height: 22, marginLeft: 8 }}>
                      <input
                        id={`color-input-${idx}`}
                        type="color"
                        aria-label={`Farbe für Team ${label}`}
                        value={colorInputValue}
                        onChange={(e) => updateTeamColor(idx, e.target.value)}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 'none', padding: 0, margin: 0 }}
                      />
                      {/* Keep the visible button for styling and label semantics */}
                      <label
                        htmlFor={`color-input-${idx}`}
                        className="manual-button"
                        aria-label={`Manuelle Farbauswahl für Team ${label}`}
                        title="Manuelle Farbauswahl"
                        role="button"
                        style={{ position: 'relative', zIndex: 1 }}
                      >
                        M
                      </label>
                    </span>
                    {/* Transparent toggle: set explicit transparent (null) so other code can treat it as no color */}
                    <button
                      type="button"
                      className="transparent-button"
                      aria-label={`Transparent für Team ${label}`}
                      title="Transparent (keine Farbe)"
                      onClick={() => updateTeamColor(idx, null)}
                      style={{ marginLeft: 6 }}
                    >
                      {/* Diagonal stripes icon (inline SVG) */}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <defs>
                          <pattern id={`stripes-${idx}`} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                            <rect width="3" height="6" fill="rgba(0,0,0,0.12)" />
                          </pattern>
                        </defs>
                        <rect x="0" y="0" width="24" height="24" fill={`url(#stripes-${idx})`} rx="3" />
                      </svg>
                      <span className="sr-only">Transparent</span>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SettingsSummary;
